import { useEffect, useRef } from 'react';
import { formatCurrency } from '@sailpoint/bva-core';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';


Chart.register(ChartDataLabels);

export default function BarChart({ results, selectedYears = new Set(), onYearSelect, canvasRef }) {
    // Use the passed-in canvasRef directly for export compatibility
    const chartInstance = useRef(null);
    const tooltipState = useRef({ visible: false, top: '0px', left: 'auto', right: 'auto', color: '#000', title: '', body: '' });

    function formatAxisValue(value, currencySymbol = '$') {
        if (value >= 1_000_000) return `${currencySymbol}${Math.round(value / 1_000_000)}M`;
        if (value >= 1_000) return `${currencySymbol}${Math.round(value / 1_000)}K`;
        return `${currencySymbol}${value}`;
    }

    function externalTooltipHandler(context) {
        const { chart, tooltip } = context;
        if (tooltip.opacity === 0) {
            tooltipState.current.visible = false;
            return;
        }
        const dataPoint = tooltip.dataPoints[0];
        if (dataPoint) {
            const { label, parsed } = dataPoint;
            tooltipState.current.title = label || '';
            tooltipState.current.body = formatCurrency(parsed.y, results?.currencySymbol || '$');
            tooltipState.current.color = '#0033a1';
        }
        //const chartArea = chart.chartArea;
        const offset = 15;
        const tooltipWidth = 200;
        const tooltipHeight = 80;
        let left = tooltip.caretX + offset;
        let right = 'auto';
        if (left + tooltipWidth > chart.canvas.width) {
            left = 'auto';
            right = `${chart.canvas.width - tooltip.caretX + offset}px`;
        }
        if (typeof left === 'number' && left < 0) left = offset;
        let top = tooltip.caretY;
        if (top + tooltipHeight > chart.canvas.height) top = chart.canvas.height - tooltipHeight - offset;
        if (top < 0) top = offset;
        tooltipState.current.top = `${top}px`;
        tooltipState.current.left = typeof left === 'number' ? `${left}px` : left;
        tooltipState.current.right = right;
        tooltipState.current.visible = true;
    }

    useEffect(() => {
        if (!canvasRef.current || !results || !results.barChartData || !results.barChartData.length) {
            if (chartInstance.current) { chartInstance.current.destroy(); chartInstance.current = null; }
            return;
        }
        if (chartInstance.current) { chartInstance.current.destroy(); }
        const ctx = canvasRef.current.getContext('2d');
        const barGradient = ctx.createLinearGradient(0, canvasRef.current.height, 0, 0);
        barGradient.addColorStop(0, '#0033a1');
        barGradient.addColorStop(1, '#0071CE');
        const hoverGradient = ctx.createLinearGradient(0, canvasRef.current.height, 0, 0);
        hoverGradient.addColorStop(0, '#00226D');
        hoverGradient.addColorStop(1, '#005CBF');
        const barColors = results.barChartData.map(d =>
            selectedYears.size === 0 || selectedYears.has(d.year) ? barGradient : '#E5E7EB'
        );
        const hoverColors = results.barChartData.map(d =>
            selectedYears.size === 0 || selectedYears.has(d.year) ? hoverGradient : '#D1D5DB'
        );
        const chartData = {
            labels: results.barChartData.map((d) => d.year),
            datasets: [{
                label: ' Annual Savings',
                data: results.barChartData.map((d) => d.savings),
                backgroundColor: barColors,
                hoverBackgroundColor: hoverColors,
                borderWidth: 0,
                borderRadius: 6,
                borderSkipped: false
            }]
        };
        chartInstance.current = new Chart(ctx, {
            type: 'bar',
            data: chartData,
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: { padding: { top: 20, right: 20, bottom: 10, left: 10 } },
                onClick: (event, elements) => {
                    if (elements.length > 0 && onYearSelect) {
                        const clickedIndex = elements[0].index;
                        const year = chartData.labels[clickedIndex];
                        onYearSelect(year);
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            callback: (value) => formatAxisValue(Number(value), results?.currencySymbol)
                        },
                        grid: { display: false },
                        border: { display: false }
                    },
                    x: { grid: { display: false } }
                },
                plugins: {
                    legend: { display: false },
                    tooltip: { enabled: false, external: externalTooltipHandler },
                    datalabels: { display: false }
                }
            }
        });
        return () => { if (chartInstance.current) { chartInstance.current.destroy(); } };
    }, [results, selectedYears, onYearSelect]);

    return (
        <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm h-full flex flex-col" id="barChartComponent">
            <h3 className="text-xl text-gray-700 mb-4 text-center flex-shrink-0">Total Savings per Year</h3>
            <div className="flex-grow relative min-h-0">
                {results && results.barChartData && results.barChartData.length > 0 ? (
                    <div className="relative h-full">
                        <canvas ref={canvasRef} id="barChartCanvas" style={{ width: '100%', height: '300px' }} />
                        {tooltipState.current.visible && (
                            <div className="flex absolute rounded-md shadow-lg overflow-hidden transition-opacity duration-100 pointer-events-none"
                                style={{ top: tooltipState.current.top, left: tooltipState.current.left, right: tooltipState.current.right, transform: 'translateY(-50%)' }}>
                                <div className="w-1.5" style={{ backgroundColor: tooltipState.current.color }} />
                                <div className="bg-slate-800 text-white p-3 text-left">
                                    <div className="font-bold text-sm mb-1">{tooltipState.current.title}</div>
                                    <div className="text-xs">{tooltipState.current.body}</div>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"> <path strokeLinecap="round" strokeLinejoin="round" d="M3 13l3 3l3-3m0 0l3 3l3-3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /> </svg>
                        <p className="font-medium">No Savings Data</p>
                        <p className="text-sm">Select benefits and calculate to see your breakdown.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
